import { useState, useEffect } from 'react';
import {
  Plug, Loader2, Plus, Trash2, CheckCircle2, AlertTriangle,
  Sparkles, FlaskConical, Eye, X,
} from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Textarea from '@/components/ui/Textarea';
import Modal from '@/components/ui/Modal';
import { useUIStore } from '@/stores/ui-store';

// Mirrors functions/src/services/ai/adapter-spec/schema.ts ADAPTER_SPEC_CAPABILITIES.
// Kept in sync by hand — the backend rejects anything not in its own enum.
const CAPABILITIES = [
  { value: 'music-gen', label: 'music-gen (audio URL)' },
  { value: 'embedding', label: 'embedding' },
  { value: 'stt', label: 'stt (speech-to-text)' },
  { value: 'text', label: 'text' },
  { value: 'vision', label: 'vision' },
  { value: 'image-gen', label: 'image-gen' },
  { value: 'tts', label: 'tts (text-to-speech)' },
];

interface SpecListItem {
  id: string;
  provider_type: string;
  capability: string;
  base_url: string;
  validated: boolean;
  validated_at: string | null;
  validation_error: string | null;
  validation_sample: Record<string, unknown> | null;
  updated_at: string;
}

const API_BASE = '/admin/platform/ai-adapter-specs';

export default function AiAdapterSpecsSection() {
  const [specs, setSpecs] = useState<SpecListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const addToast = useUIStore((s) => s.addToast);

  // Synthesize dialog
  const [showSynth, setShowSynth] = useState(false);
  const [synthProvider, setSynthProvider] = useState('');
  const [synthCapability, setSynthCapability] = useState('music-gen');
  const [synthBaseUrl, setSynthBaseUrl] = useState('');
  const [synthDocs, setSynthDocs] = useState('');
  const [synthSample, setSynthSample] = useState('');
  const [synthesizing, setSynthesizing] = useState(false);

  // Validate dialog
  const [validateTarget, setValidateTarget] = useState<SpecListItem | null>(null);
  const [validateKey, setValidateKey] = useState('');
  const [validateModel, setValidateModel] = useState('');
  const [validating, setValidating] = useState(false);

  // View-spec dialog
  const [viewSpec, setViewSpec] = useState<{ id: string; json: string } | null>(null);
  const [viewLoading, setViewLoading] = useState(false);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    try {
      setLoading(true);
      const res = await api.get<{ specs: SpecListItem[] }>(API_BASE);
      setSpecs(res.specs || []);
    } catch (err) {
      console.error('Failed to load adapter specs:', err);
      addToast('Failed to load adapter specs', 'error');
    } finally {
      setLoading(false);
    }
  }

  function openSynth() {
    setSynthProvider('');
    setSynthCapability('music-gen');
    setSynthBaseUrl('');
    setSynthDocs('');
    setSynthSample('');
    setShowSynth(true);
  }

  async function handleSynthesize() {
    if (!synthProvider.trim() || !/^https?:\/\//.test(synthBaseUrl.trim())) {
      addToast('Provider type and an http(s) base URL are required', 'error');
      return;
    }
    setSynthesizing(true);
    try {
      await api.post(`${API_BASE}/synthesize`, {
        provider_type: synthProvider.trim(),
        capability: synthCapability,
        base_url: synthBaseUrl.trim(),
        ...(synthDocs.trim() ? { docs: synthDocs.trim() } : {}),
        ...(synthSample.trim() ? { sample: synthSample.trim() } : {}),
      });
      addToast('Spec synthesized (unvalidated) — run Validate next', 'success');
      setShowSynth(false);
      await load();
    } catch (err: any) {
      addToast(err?.message || 'Synthesis failed', 'error');
    } finally {
      setSynthesizing(false);
    }
  }

  function openValidate(spec: SpecListItem) {
    setValidateTarget(spec);
    setValidateKey('');
    setValidateModel('');
  }

  async function handleValidate() {
    if (!validateTarget || !validateKey.trim() || !validateModel.trim()) {
      addToast('API key and model are required', 'error');
      return;
    }
    setValidating(true);
    try {
      const res = await api.post<{ validated: boolean; error: string | null }>(
        `${API_BASE}/${validateTarget.id}/validate`,
        { api_key: validateKey.trim(), model: validateModel.trim() },
      );
      addToast(
        res.validated ? 'Validation passed — spec is now dispatchable' : `Validation failed: ${res.error ?? 'unknown'}`,
        res.validated ? 'success' : 'error',
      );
      setValidateTarget(null);
      await load();
    } catch (err: any) {
      addToast(err?.message || 'Validation request failed', 'error');
    } finally {
      setValidating(false);
    }
  }

  async function handleView(spec: SpecListItem) {
    setViewSpec({ id: spec.id, json: '' });
    setViewLoading(true);
    try {
      const res = await api.get<{ spec: { spec: unknown } }>(`${API_BASE}/${spec.id}`);
      setViewSpec({ id: spec.id, json: JSON.stringify(res.spec?.spec ?? res.spec, null, 2) });
    } catch {
      addToast('Failed to load full spec', 'error');
      setViewSpec(null);
    } finally {
      setViewLoading(false);
    }
  }

  async function handleDelete(spec: SpecListItem) {
    if (!window.confirm(`Delete the ${spec.provider_type} / ${spec.capability} adapter spec?`)) return;
    try {
      await api.delete(`${API_BASE}/${spec.id}`);
      addToast('Adapter spec deleted', 'success');
      await load();
    } catch {
      addToast('Failed to delete adapter spec', 'error');
    }
  }

  if (loading) {
    return (
      <Card padding="lg" className="mb-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </div>
      </Card>
    );
  }

  return (
    <Card padding="lg" className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Plug className="w-5 h-5 text-purple-500" />
          <h3 className="text-lg font-semibold text-gray-900">AI Adapter Specs</h3>
        </div>
        <Button variant="primary" size="sm" type="button" onClick={openSynth}>
          <Plus className="w-4 h-4 mr-1" /> Synthesize
        </Button>
      </div>

      <p className="text-sm text-gray-500 mb-3">
        Declarative adapter specs let RPAAD call bespoke providers it has no native adapter for
        (e.g. a music-gen provider). An LLM synthesizes a JSON spec from the provider's docs; a
        fixed interpreter runs it — no model-authored code is ever executed.
      </p>
      <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>
          A spec only dispatches once it is <strong>validated</strong> AND the{' '}
          <code className="font-mono">RPAAD_ADAPTER_SYNTHESIS</code> flag is enabled on the
          platform container. Synthesized specs start unvalidated — run <strong>Validate</strong> with
          a real API key + model to flip them.
        </span>
      </div>

      {specs.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p className="font-medium">No adapter specs yet</p>
          <p className="text-sm mt-1">Synthesize one to teach RPAAD a bespoke provider.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {specs.map((spec) => (
            <div key={spec.id} className="rounded-lg border border-gray-200 bg-white px-3 py-2.5">
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-gray-900">{spec.provider_type}</span>
                    <span className="text-xs text-gray-400">·</span>
                    <span className="text-xs font-mono text-gray-600">{spec.capability}</span>
                    {spec.validated ? (
                      <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded">
                        <CheckCircle2 className="w-3 h-3" /> validated
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                        <AlertTriangle className="w-3 h-3" /> unvalidated
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 font-mono truncate mt-0.5">{spec.base_url}</div>
                  {spec.validation_error && (
                    <div className="text-xs text-red-600 mt-0.5">Error: {spec.validation_error}</div>
                  )}
                  {spec.validated && spec.validation_sample && (
                    <div className="text-xs text-gray-500 mt-0.5 font-mono">
                      sample: {JSON.stringify(spec.validation_sample)}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => handleView(spec)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                    title="View full spec"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => openValidate(spec)}
                    className="p-1.5 text-gray-400 hover:text-purple-600 rounded"
                    title="Validate"
                  >
                    <FlaskConical className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(spec)}
                    className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Synthesize dialog */}
      <Modal isOpen={showSynth} onClose={() => setShowSynth(false)} title="Synthesize Adapter Spec">
        <div className="space-y-4">
          <Input
            label="Provider type"
            value={synthProvider}
            onChange={(e) => setSynthProvider(e.target.value)}
            placeholder="e.g. suno"
            required
          />
          <Select
            label="Capability"
            value={synthCapability}
            onChange={(e) => setSynthCapability(e.target.value)}
            options={CAPABILITIES}
          />
          <Input
            label="API base URL"
            value={synthBaseUrl}
            onChange={(e) => setSynthBaseUrl(e.target.value)}
            placeholder="https://api.example.com/v1"
            required
          />
          <Textarea
            label="API docs excerpt (optional)"
            value={synthDocs}
            onChange={(e) => setSynthDocs(e.target.value)}
            rows={4}
            placeholder="Paste the relevant endpoint/auth/response docs to ground the synthesis."
          />
          <Textarea
            label="Sample response (optional)"
            value={synthSample}
            onChange={(e) => setSynthSample(e.target.value)}
            rows={3}
            placeholder="An example provider response, to help map the response paths."
          />
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Sparkles className="w-3.5 h-3.5 text-purple-500" />
            Synthesis runs an LLM and stores the spec unvalidated.
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" type="button" onClick={() => setShowSynth(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="button" onClick={handleSynthesize} isLoading={synthesizing}>
              Synthesize
            </Button>
          </div>
        </div>
      </Modal>

      {/* Validate dialog */}
      <Modal
        isOpen={!!validateTarget}
        onClose={() => setValidateTarget(null)}
        title={validateTarget ? `Validate ${validateTarget.provider_type} / ${validateTarget.capability}` : 'Validate'}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Runs one egress-locked probe request against the live provider with the key + model
            below, then flips the spec to validated if the response shape checks out. The key is
            used only for this probe and is not stored.
          </p>
          <Input
            label="API key"
            type="password"
            value={validateKey}
            onChange={(e) => setValidateKey(e.target.value)}
            placeholder="Provider API key"
            required
          />
          <Input
            label="Model"
            value={validateModel}
            onChange={(e) => setValidateModel(e.target.value)}
            placeholder="A model the provider serves for this capability"
            required
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" type="button" onClick={() => setValidateTarget(null)}>
              Cancel
            </Button>
            <Button variant="primary" type="button" onClick={handleValidate} isLoading={validating}>
              <FlaskConical className="w-4 h-4 mr-1" /> Run probe
            </Button>
          </div>
        </div>
      </Modal>

      {/* View-spec dialog */}
      <Modal isOpen={!!viewSpec} onClose={() => setViewSpec(null)} title="Adapter Spec">
        {viewLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : (
          <pre className="text-xs bg-gray-900 text-gray-100 rounded-md p-3 overflow-auto max-h-[60vh]">
            {viewSpec?.json}
          </pre>
        )}
        <div className="flex justify-end pt-3">
          <Button variant="outline" type="button" onClick={() => setViewSpec(null)}>
            <X className="w-4 h-4 mr-1" /> Close
          </Button>
        </div>
      </Modal>
    </Card>
  );
}
